import { resolveProfileThemeFontFamily } from "../constants/profileThemeFonts";
import {
  clamp,
  DEFAULT_BORDER_RADIUS,
  DEFAULT_DARK_TEXT,
  DEFAULT_LIGHT_TEXT,
  DEFAULT_TEXT_COLOR,
  hexToRgba,
  isHexColor,
  mixHexColors,
  normalizeEnum,
  relativeLuminance,
  resolveBorderRadius,
  resolveReadableColor,
  resolveShadowValue,
  toNumber,
} from "./themeColor";

// Re-exported so existing importers of this module keep working unchanged.
export {
  clamp,
  contrastRatio,
  findContrastCandidate,
  hexToRgb,
  hexToRgba,
  isHexColor,
  mixHexColors,
  normalizeEnum,
  relativeLuminance,
  resolveBorderRadius,
  resolveReadableColor,
  resolveShadowValue,
  rgbToHex,
  toNumber,
} from "./themeColor";

export const DEFAULT_CONTENT_WIDTH = 1120;
export const CONTENT_WIDTH_MIN = 880;
export const CONTENT_WIDTH_MAX = 1320;

export const DEFAULT_PUBLIC_PROFILE_THEME = {
  backgroundColor: "#ffffff",
  sectionBackgroundColor: "transparent",
  serviceCardBackgroundColor: "transparent",
  textColor: DEFAULT_TEXT_COLOR,
  secondaryTextColor: "#475569",
  accentColor: "#111111",
  primaryColor: "#4f46e5",
  borderColor: "#e2e8f0",
  borderRadius: DEFAULT_BORDER_RADIUS,
  borderStyle: "subtle",
  shadowStyle: "soft",
  spacingDensity: "balanced",
  servicesLayout: "card",
  contentWidth: DEFAULT_CONTENT_WIDTH,
  backgroundShape: "circles",
  backgroundShapeDensity: "subtle",
  backgroundShapeColor: "surface",
  headerLayout: "standard",
  fontFamily: "Inter",
  servicesHeading: "",
  primaryCtaText: "",
};

const TRANSPARENT_COLOR_TOKEN = "transparent";

// Exported so the two builder surfaces offer exactly what the renderer accepts,
// rather than keeping their own copies that can drift apart.
export const BORDER_STYLE_OPTIONS = ["none", "subtle"];
export const SHADOW_STYLE_OPTIONS = ["none", "soft", "lifted"];
export const SPACING_DENSITY_OPTIONS = ["comfortable", "balanced", "compact"];
// Every layout a saved theme may hold. "list" and "grid" are drawn on Custom
// pages only (the templates use them); a Standard page draws them as "card",
// and Standard's own picker offers just the first two.
export const SERVICES_LAYOUT_OPTIONS = ["card", "minimal", "list", "grid"];
export const STANDARD_SERVICES_LAYOUT_OPTIONS = ["card", "minimal"];
export const CUSTOM_ONLY_SERVICES_LAYOUTS = ["list", "grid"];
// "images" swaps a Custom page's header for the pro's header images.
export const HEADER_LAYOUT_OPTIONS = ["standard", "images"];

export const BACKGROUND_SHAPE_OPTIONS = ["none", "circles", "blobs", "stars"];
export const BACKGROUND_SHAPE_DENSITY_OPTIONS = ["subtle", "medium"];
export const BACKGROUND_SHAPE_COLOR_OPTIONS = [
  "accent",
  "secondary",
  "surface",
  "border",
];

// Kept low so decorative shapes never compete with profile text.
const BACKGROUND_SHAPE_ALPHA = 0.28;

const isTransparentColor = (value) =>
  typeof value === "string" && value.trim().toLowerCase() === TRANSPARENT_COLOR_TOKEN;

const normalizeColor = (value, fallbackValue) => {
  if (typeof value !== "string") {
    return fallbackValue;
  }
  const normalized = value.trim();
  return isHexColor(normalized) ? normalized : fallbackValue;
};

const normalizeSectionBackgroundColor = (value, fallbackValue) => {
  if (typeof value !== "string") {
    return fallbackValue;
  }

  const normalized = value.trim();
  if (isTransparentColor(normalized)) {
    return TRANSPARENT_COLOR_TOKEN;
  }

  return isHexColor(normalized) ? normalized : fallbackValue;
};

const normalizeServiceCardBackgroundColor = (value, fallbackValue) => {
  if (typeof value !== "string") {
    return fallbackValue;
  }

  const normalized = value.trim();
  if (isTransparentColor(normalized)) {
    return TRANSPARENT_COLOR_TOKEN;
  }

  return isHexColor(normalized) ? normalized : fallbackValue;
};

const resolveAccentColor = (theme = {}) => {
  const explicitAccent = normalizeColor(theme.accentColor, "");
  if (explicitAccent) {
    return explicitAccent;
  }

  const primaryColor = normalizeColor(theme.primaryColor, "");
  const defaultPrimary = DEFAULT_PUBLIC_PROFILE_THEME.primaryColor.toLowerCase();
  const isCustomPrimary = Boolean(
    primaryColor && primaryColor.toLowerCase() !== defaultPrimary
  );

  return isCustomPrimary ? primaryColor : DEFAULT_PUBLIC_PROFILE_THEME.accentColor;
};

const resolveSoftSurfaceColor = (sectionBackgroundColor) => {
  if (!isHexColor(sectionBackgroundColor)) {
    return "#f7f9fc";
  }
  const luminance = relativeLuminance(sectionBackgroundColor);
  if (luminance < 0.25) {
    return mixHexColors(sectionBackgroundColor, "#ffffff", 0.15);
  }
  return mixHexColors(sectionBackgroundColor, "#f8fafc", 0.55);
};

export const resolveContentWidth = (value) => {
  const parsed = toNumber(value);
  if (parsed === null) {
    return DEFAULT_CONTENT_WIDTH;
  }
  return clamp(Math.floor(parsed), CONTENT_WIDTH_MIN, CONTENT_WIDTH_MAX);
};

const resolveBackgroundShapeColor = (source, palette) =>
  hexToRgba(palette[source], BACKGROUND_SHAPE_ALPHA);

// `isCustom` is whether the page is a Custom one. Without it, the Custom-only
// values (list and grid services, the images header) fall back to what a
// Standard page has always drawn.
export const buildPublicProfileTheme = (rawTheme = {}, { isCustom = false } = {}) => {
  // A stored colour is always honoured. The "legacy white" downgrade that used to
  // sit here rewrote a stored #ffffff to transparent unless a `*Customized` flag
  // said otherwise — and that flag never persisted, so picking white silently gave
  // you transparent. The server default (DEFAULT_THEME in server/models/user.js) is
  // the single source of truth for what an unset colour means.
  const sectionBackgroundRawValue = rawTheme.sectionBackgroundColor;
  const serviceCardBackgroundRawValue = rawTheme.serviceCardBackgroundColor;

  const backgroundColor = normalizeColor(
    rawTheme.backgroundColor,
    DEFAULT_PUBLIC_PROFILE_THEME.backgroundColor
  );
  const sectionBackgroundColor = normalizeSectionBackgroundColor(
    sectionBackgroundRawValue,
    DEFAULT_PUBLIC_PROFILE_THEME.sectionBackgroundColor
  );
  const resolvedSectionHexBackground = isTransparentColor(sectionBackgroundColor)
    ? backgroundColor
    : sectionBackgroundColor;
  const serviceCardBackgroundColor = normalizeServiceCardBackgroundColor(
    serviceCardBackgroundRawValue,
    DEFAULT_PUBLIC_PROFILE_THEME.serviceCardBackgroundColor
  );
  const borderColor = normalizeColor(
    rawTheme.borderColor,
    DEFAULT_PUBLIC_PROFILE_THEME.borderColor
  );
  const accentColor = resolveAccentColor(rawTheme);
  const primaryTextColor = normalizeColor(
    rawTheme.textColor,
    DEFAULT_PUBLIC_PROFILE_THEME.textColor
  );
  const secondaryTextColor = normalizeColor(
    rawTheme.secondaryTextColor,
    DEFAULT_PUBLIC_PROFILE_THEME.secondaryTextColor
  );
  const ctaTextColor = resolveReadableColor(accentColor, DEFAULT_LIGHT_TEXT, [
    DEFAULT_LIGHT_TEXT,
    DEFAULT_DARK_TEXT,
  ]);
  const ctaHoverColor = mixHexColors(accentColor, DEFAULT_DARK_TEXT, 0.18);
  const borderRadius = resolveBorderRadius(rawTheme.borderRadius);
  const borderStyle = normalizeEnum(
    rawTheme.borderStyle,
    BORDER_STYLE_OPTIONS,
    DEFAULT_PUBLIC_PROFILE_THEME.borderStyle
  );
  const shadowStyle = normalizeEnum(
    rawTheme.shadowStyle,
    SHADOW_STYLE_OPTIONS,
    DEFAULT_PUBLIC_PROFILE_THEME.shadowStyle
  );
  const spacingDensity = normalizeEnum(
    rawTheme.spacingDensity,
    SPACING_DENSITY_OPTIONS,
    DEFAULT_PUBLIC_PROFILE_THEME.spacingDensity
  );
  const savedServicesLayout = normalizeEnum(
    rawTheme.servicesLayout,
    SERVICES_LAYOUT_OPTIONS,
    DEFAULT_PUBLIC_PROFILE_THEME.servicesLayout
  );
  const servicesLayout =
    !isCustom && CUSTOM_ONLY_SERVICES_LAYOUTS.includes(savedServicesLayout)
      ? DEFAULT_PUBLIC_PROFILE_THEME.servicesLayout
      : savedServicesLayout;
  const headerLayout = isCustom
    ? normalizeEnum(
        rawTheme.headerLayout,
        HEADER_LAYOUT_OPTIONS,
        DEFAULT_PUBLIC_PROFILE_THEME.headerLayout
      )
    : DEFAULT_PUBLIC_PROFILE_THEME.headerLayout;
  const contentWidth = resolveContentWidth(rawTheme.contentWidth);
  const backgroundShape = normalizeEnum(
    rawTheme.backgroundShape,
    BACKGROUND_SHAPE_OPTIONS,
    DEFAULT_PUBLIC_PROFILE_THEME.backgroundShape
  );
  const backgroundShapeDensity = normalizeEnum(
    rawTheme.backgroundShapeDensity,
    BACKGROUND_SHAPE_DENSITY_OPTIONS,
    DEFAULT_PUBLIC_PROFILE_THEME.backgroundShapeDensity
  );
  const backgroundShapeColorSource = normalizeEnum(
    rawTheme.backgroundShapeColor,
    BACKGROUND_SHAPE_COLOR_OPTIONS,
    DEFAULT_PUBLIC_PROFILE_THEME.backgroundShapeColor
  );
  const fontFamily = resolveProfileThemeFontFamily(rawTheme.fontFamily);
  const softSurfaceColor = normalizeColor(
    rawTheme.softSurfaceColor,
    resolveSoftSurfaceColor(resolvedSectionHexBackground)
  );
  const backgroundShapeColor = resolveBackgroundShapeColor(backgroundShapeColorSource, {
    accent: accentColor,
    secondary: secondaryTextColor,
    surface: softSurfaceColor,
    border: borderColor,
  });
  // The booking panel is the one raised surface the design keeps, and it takes
  // the section background straight. Only when that is left transparent does it
  // fall back to the derived soft surface, so the card still reads as a card
  // against a page it would otherwise match exactly.
  const panelSurfaceColor = isTransparentColor(sectionBackgroundColor)
    ? softSurfaceColor
    : sectionBackgroundColor;
  const serviceShadow = resolveShadowValue(shadowStyle);
  const sectionShadow = shadowStyle === "none" ? "none" : resolveShadowValue("soft");

  const cssVariables = {
    "--profile-background-color": backgroundColor,
    "--profile-text-color": primaryTextColor,
    "--profile-secondary-text-color": secondaryTextColor,
    "--profile-muted-text-color": secondaryTextColor,
    "--profile-surface-color": sectionBackgroundColor,
    "--profile-soft-surface": softSurfaceColor,
    "--profile-panel-surface": panelSurfaceColor,
    "--profile-font-family": fontFamily,
    "--profile-accent-color": accentColor,
    "--profile-cta-bg": accentColor,
    "--profile-cta-bg-hover": ctaHoverColor,
    "--profile-cta-text": ctaTextColor,
    "--profile-border-color": borderColor,
    "--profile-outline-color": hexToRgba(borderColor, 0.6),
    "--profile-card-border": hexToRgba(borderColor, 0.9),
    "--profile-tag-bg": hexToRgba(primaryTextColor, 0.08),
    "--profile-services-section-bg": sectionBackgroundColor,
    "--profile-service-card-bg": serviceCardBackgroundColor,
    "--profile-service-border-color":
      borderStyle === "none" ? "transparent" : hexToRgba(borderColor, 0.9),
    // Lines between rows inside a card (Usual hours, Contact, Questions). With
    // no borders the rows are still split, by the text colour faded right down.
    "--profile-divider-color":
      borderStyle === "none" ? hexToRgba(primaryTextColor, 0.22) : hexToRgba(borderColor, 0.9),
    "--profile-service-shadow": serviceShadow,
    "--profile-section-shadow": sectionShadow,
    "--profile-border-radius": `${borderRadius}px`,
    "--profile-service-border-radius": `${Math.max(borderRadius - 2, 8)}px`,
    "--profile-content-width": `${contentWidth}px`,
    "--profile-shape-color": backgroundShapeColor,
  };

  return {
    cssVariables,
    servicesLayout,
    headerLayout,
    classes: {
      servicesLayout: `services-layout-${servicesLayout}`,
      spacingDensity: `services-density-${spacingDensity}`,
      borderStyle: `services-border-${borderStyle}`,
      shadowStyle: `services-shadow-${shadowStyle}`,
      backgroundShape: `profile-shape-${backgroundShape}`,
      backgroundShapeDensity: `profile-shape-density-${backgroundShapeDensity}`,
    },
    servicesClassName: [
      `services-layout-${servicesLayout}`,
      `services-density-${spacingDensity}`,
      `services-border-${borderStyle}`,
      `services-shadow-${shadowStyle}`,
    ].join(" "),
  };
};

export default buildPublicProfileTheme;
